/**
 * Device Flow Handler using oidc-client-ts
 * 
 * Implements OAuth 2.0 Device Authorization Grant flow with Amazon Federate.
 * Uses redirect-based authentication (no polling required) with PKCE verification.
 */

import { UserManager, UserManagerSettings, User, WebStorageStateStore } from 'oidc-client-ts';
import { v4 as uuidv4 } from 'uuid';
import { 
  DeviceFlowHandler as IDeviceFlowHandler,
  DeviceFlowInitiationParams,
  TokenResponse,
  AuthValidationResult,
  JWTTokens,
  UserInfo,
  FEDERATE_CONFIG
} from '../types/deviceFlowTypes';
import { getPayloadValue as getVal, extractLdapGroups } from '../utils/jwtPayloadMapper';

export class DeviceFlowHandler implements IDeviceFlowHandler {
  public userManager: UserManager;
  private callbackInProgress: boolean = false;

  constructor() {
    console.log('[DeviceFlowHandler] Initializing with config:', {
      clientId: FEDERATE_CONFIG.CLIENT_ID,
      authority: FEDERATE_CONFIG.AUTHORITY,
      redirectUri: FEDERATE_CONFIG.REDIRECT_URI,
      scopes: FEDERATE_CONFIG.SCOPES
    });

    // Configure oidc-client-ts UserManager for Amazon Federate
    const settings: UserManagerSettings = {
      authority: FEDERATE_CONFIG.AUTHORITY,
      client_id: FEDERATE_CONFIG.CLIENT_ID,
      redirect_uri: FEDERATE_CONFIG.REDIRECT_URI,
      response_type: FEDERATE_CONFIG.RESPONSE_TYPE,
      scope: FEDERATE_CONFIG.SCOPES.join(' '),
      post_logout_redirect_uri: window.location.origin,
      
      // Custom endpoints since Amazon Federate may not have standard discovery
      metadata: {
        issuer: FEDERATE_CONFIG.AUTHORITY,
        authorization_endpoint: FEDERATE_CONFIG.AUTHORIZE_ENDPOINT,
        token_endpoint: FEDERATE_CONFIG.TOKEN_ENDPOINT,
      },
      
      // PKCE configuration for security (enabled by default in oidc-client-ts)
      response_mode: 'query',
      loadUserInfo: false, // We'll extract user info manually from tokens
      
      // Use custom localStorage-based store instead of default sessionStorage
      // This ensures tokens persist when PWA is killed and restarted
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      
      // Automatic silent renewal configuration
      automaticSilentRenew: false, // We'll handle refresh manually
      
      // Additional security settings
      filterProtocolClaims: true
    };

    this.userManager = new UserManager(settings);

    // Set up event handlers
    this.setupEventHandlers();
  }

  /**
   * Set up event handlers for the UserManager
   */
  private setupEventHandlers(): void {
    this.userManager.events.addUserLoaded((user: User) => {
      console.log('[DeviceFlow] User loaded:', {
        profile: user.profile,
        expired: user.expired,
        expiresIn: user.expires_in
      });
    });

    this.userManager.events.addUserUnloaded(() => {
      console.log('[DeviceFlow] User unloaded');
    });

    this.userManager.events.addAccessTokenExpiring(() => {
      console.log('[DeviceFlow] Access token expiring');
    });

    this.userManager.events.addAccessTokenExpired(() => {
      console.log('[DeviceFlow] Access token expired');
    });

    this.userManager.events.addUserSignedOut(() => {
      console.log('[DeviceFlow] User signed out');
    });
  }

  /**
   * Initiates the device flow by redirecting to Amazon Federate
   * Uses oidc-client-ts for PKCE and state management
   */
  async initiateDeviceFlow(params: DeviceFlowInitiationParams = {}): Promise<void> {
    const startTime = Date.now();
    
    // Check if we're already redirecting
    const isRedirecting = sessionStorage.getItem('deviceFlowRedirecting');
    const redirectingTimestamp = sessionStorage.getItem('deviceFlowRedirectingTimestamp');
    
    if (isRedirecting === 'true' && redirectingTimestamp) {
      const timeSinceRedirect = Date.now() - parseInt(redirectingTimestamp);
      if (timeSinceRedirect > 5000) {
        console.log('[DeviceFlow] Clearing stale redirect flag (timeout)');
        sessionStorage.removeItem('deviceFlowRedirecting');
        sessionStorage.removeItem('deviceFlowRedirectingTimestamp');
      } else {
        console.log('[DeviceFlow] Preventing duplicate redirect - already in progress');
        return;
      }
    }
    
    try {
      // Mark that we're starting the redirect process
      sessionStorage.setItem('deviceFlowRedirecting', 'true');
      sessionStorage.setItem('deviceFlowRedirectingTimestamp', Date.now().toString());
      
      console.log('[DeviceFlow] Starting device flow initiation with oidc-client-ts');

      // Store the client request ID for tracking if provided
      if (params.clientRequestId) {
        sessionStorage.setItem("deviceFlowClientRequestId", params.clientRequestId);
      }
      
      // Store the current location pathname for potential redirect back
      const currentPath = window.location.pathname;
      const originPath = currentPath === '/' ? '/' : currentPath;
      sessionStorage.setItem("deviceFlowOrigin", originPath);
      
      console.log('[DeviceFlow] Stored origin path for redirect:', originPath);
      
      // Use oidc-client-ts to create the authorization request
      // This automatically handles PKCE code_verifier/code_challenge generation and state management
      await this.userManager.signinRedirect({
        state: params.state || uuidv4(),
      });
      
      // Set a timeout to clear the redirecting flag in case redirect fails
      setTimeout(() => {
        sessionStorage.removeItem('deviceFlowRedirecting');
        sessionStorage.removeItem('deviceFlowRedirectingTimestamp');
      }, 10000);
      
      console.log(`[DeviceFlow] Device flow initiation completed in ${Date.now() - startTime}ms`);
    } catch (error) {
      // Clear the redirecting flag on error
      sessionStorage.removeItem('deviceFlowRedirecting');
      sessionStorage.removeItem('deviceFlowRedirectingTimestamp');
      console.error('[DeviceFlow] Error during initiation:', error);
      throw new Error(`Failed to initiate device flow: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Handles the callback from Amazon Federate and processes the authorization code
   * Uses oidc-client-ts for token exchange and validation
   */
  async handleCallback(authorizationCode?: string): Promise<TokenResponse> {
    // Prevent duplicate callback processing
    if (this.callbackInProgress) {
      console.log('[DeviceFlow] Callback already in progress, skipping duplicate request');
      throw new Error('Callback processing already in progress');
    }

    this.callbackInProgress = true;
    const startTime = Date.now();
    
    console.log('[DeviceFlow] Starting callback processing with oidc-client-ts');

    try {
      // Use oidc-client-ts to process the signin response
      // This automatically handles PKCE verification and token exchange
      const user = await this.userManager.signinCallback(window.location.href);
      
      if (!user || user.expired) {
        throw new Error("Invalid or expired user session from callback");
      }

      console.log('[DeviceFlow] Signin callback successful:', {
        profile: user.profile,
        expired: user.expired,
        expiresIn: user.expires_in,
        tokenType: user.token_type
      });

      // Extract tokens from the user object
      const tokens: JWTTokens = {
        idToken: user.id_token || "",
        accessToken: user.access_token || "",
        refreshToken: user.refresh_token || "",
        expiresAt: user.expires_at ? user.expires_at * 1000 : Date.now() + (15 * 60 * 1000)
      };

      // Extract user info using existing logic
      const userInfo = this.extractUserInfoFromTokens(tokens);
      
      // Tokens are automatically stored by oidc-client-ts via WebStorageStateStore (line 70)
      // No manual storage needed - oidc-client-ts handles it in localStorage['oidc.user:...']
      console.log('[DeviceFlow] Tokens stored successfully by oidc-client-ts');
      console.log(`[DeviceFlow] Callback processing completed in ${Date.now() - startTime}ms`);
      
      return {
        ...tokens,
        userInfo
      };
    } catch (error) {
      console.error('[DeviceFlow] Callback processing failed:', error);
      throw new Error(`Token exchange failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      // Reset the flag regardless of success or failure
      this.callbackInProgress = false;
    }
  }

  /**
   * Extracts user information from JWT tokens.
   */
  private extractUserInfoFromTokens(tokens: JWTTokens): UserInfo {
    try {
      console.log('[DeviceFlow] Starting user info extraction');
      
      // Decode the ID token payload
      const base64Payload = tokens.idToken.split('.')[1];
      const payload = JSON.parse(atob(base64Payload));

      console.log('[DeviceFlow] Decoded token payload:', payload);

      const email = getVal(payload, "email");

      const userInfo: UserInfo = {
        userAlias:
          (payload?.identities as any)?.[0]?.userId ||
          getVal(payload, "preferred_username") ||
          email?.split("@")[0] ||
          getVal(payload, "uid") ||
          getVal(payload, "sub") ||
          "unknown",
        email,
        firstName: getVal(payload, "given_name"),
        lastName: getVal(payload, "family_name"),
        siteLocation: getVal(payload, "location") || getVal(payload, "custom:location"),
        ldapGroups: extractLdapGroups(payload),
      };

      console.log('[DeviceFlow] User info extracted successfully:', {
        userAlias: userInfo.userAlias,
        email: userInfo.email,
        ldapGroups: userInfo.ldapGroups
      });

      return userInfo;
    } catch (error) {
      console.error('[DeviceFlow] User info extraction failed:', error);
      throw new Error("Invalid token format: unable to decode user information");
    }
  }

  /**
   * Validates current authentication state using oidc-client-ts
   */
  async validateAuthenticationState(): Promise<AuthValidationResult> {
    try {
      const user = await this.userManager.getUser();
      
      console.log('[DeviceFlow] Authentication state validation:', {
        hasUser: !!user,
        expired: user?.expired,
        expiresIn: user?.expires_in
      });
      
      if (!user) {
        return {
          isValid: false,
          reason: "No authentication session found",
          shouldClear: true
        };
      }
      
      if (user.expired) {
        return {
          isValid: false,
          reason: `Authentication has expired at ${user.expires_at ? new Date(user.expires_at * 1000).toLocaleString() : 'unknown time'}`,
          shouldClear: true
        };
      }
      
      // Check if tokens are close to expiration (within 5 minutes)
      const expiresAt = user.expires_at;
      if (expiresAt) {
        const timeRemaining = (expiresAt * 1000) - Date.now();
        if (timeRemaining < 5 * 60 * 1000) {
          return {
            isValid: false,
            reason: `Authentication will expire soon (in ${Math.round(timeRemaining / 60000)} minutes)`,
            shouldClear: false
          };
        }
      }
      
      console.log('[DeviceFlow] Validation successful - tokens are valid');
      return { isValid: true, shouldClear: false };
    } catch (error) {
      console.error('[DeviceFlow] Validation error:', error);
      return {
        isValid: false,
        reason: "Failed to validate authentication tokens",
        shouldClear: true
      };
    }
  }

  /**
   * Refreshes tokens using oidc-client-ts
   */
  async refreshTokens(): Promise<JWTTokens> {
    const startTime = Date.now();
    
    const REFRESH_TIMEOUT_MS = 10_000;

    console.log('[DeviceFlow] Starting token refresh with oidc-client-ts');

    try {
      const user = await this.userManager.getUser();
      
      if (!user || !user.refresh_token) {
        throw new Error("No refresh token available");
      }

      // Use oidc-client-ts to refresh tokens with timeout to prevent hanging
      const refreshedUser = await Promise.race([
        this.userManager.signinSilent(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Token refresh timed out')), REFRESH_TIMEOUT_MS)
        )
      ]);
      
      if (!refreshedUser || refreshedUser.expired) {
        throw new Error("Token refresh failed or returned expired tokens");
      }

      console.log('[DeviceFlow] Token refresh successful:', {
        expired: refreshedUser.expired,
        expiresIn: refreshedUser.expires_in
      });

      const newTokens: JWTTokens = {
        idToken: refreshedUser.id_token || user.id_token || "",
        accessToken: refreshedUser.access_token || "",
        refreshToken: refreshedUser.refresh_token || user.refresh_token || "",
        expiresAt: refreshedUser.expires_at ? refreshedUser.expires_at * 1000 : Date.now() + (15 * 60 * 1000)
      };

      // Tokens are automatically stored by oidc-client-ts via WebStorageStateStore
      // No manual storage needed
      console.log(`[DeviceFlow] Token refresh completed in ${Date.now() - startTime}ms`);

      return newTokens;
    } catch (error) {
      console.error('[DeviceFlow] Token refresh failed:', error);
      
      // If refresh fails, clear all tokens
      await this.clearInvalidAuthenticationState();
      
      throw error;
    }
  }

  /**
   * Clears invalid authentication state from storage
   */
  async clearInvalidAuthenticationState(): Promise<void> {
    try {
      console.log('[DeviceFlow] Clearing invalid authentication state');
      
      // Use oidc-client-ts to remove user (automatically clears oidc.user:... key)
      await this.userManager.removeUser();
      
      // Clear session storage items related to device flow
      const sessionKeys = ["deviceFlowClientRequestId", "deviceFlowOrigin", "deviceFlowRedirecting", "deviceFlowRedirectingTimestamp"];
      sessionKeys.forEach(key => {
        if (sessionStorage.getItem(key)) {
          sessionStorage.removeItem(key);
        }
      });
      
      console.log('[DeviceFlow] Authentication state cleared successfully');
    } catch (error) {
      console.error('[DeviceFlow] Error clearing authentication state:', error);
    }
  }

  /**
   * Validates the configuration before initiating device flow
   */
  validateConfiguration(): boolean {
    const requiredFields = [
      FEDERATE_CONFIG.CLIENT_ID,
      FEDERATE_CONFIG.REDIRECT_URI,
      FEDERATE_CONFIG.AUTHORIZE_ENDPOINT,
      FEDERATE_CONFIG.TOKEN_ENDPOINT
    ];

    return requiredFields.every(field => 
      field && !field.includes("{{") && !field.includes("}}")
    );
  }
}

// Export singleton instance
export const deviceFlowHandler = new DeviceFlowHandler();
export default deviceFlowHandler;
